import { useEffect, type ReactNode } from "react";
import { createPortal } from "react-dom";
import { cn } from "@wa/ui";

/**
 * The modal wrapper: a dimmed backdrop and a centred card. Closes on Escape and
 * on a click outside the card. `children` is the card itself.
 *
 * It portals into `document.body` so it cannot land inside a surrounding
 * `<form>` (the invoice editor, for one): a nested `<form>` is invalid HTML, and
 * the modal's own "Save" would submit the outer one and reload the page. The
 * portal also sidesteps stacking- and overflow-context problems.
 *
 * The backdrop click fires on `onMouseDown` rather than `onClick` so a drag that
 * STARTS inside the card and ends outside it (selecting text, say) does not
 * close the modal - the click only counts when the press itself landed on the
 * backdrop.
 *
 * Escape does not close while a Radix popper is open (a Select, a date popover):
 * that layer gets to consume its own Escape first, so one press does not close
 * both the dropdown and everything around it.
 */
export function ModalShell({ onClose, children, className }: {
  onClose: () => void;
  children: ReactNode;
  className?: string;
}) {
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== "Escape") return;
      if (document.querySelector("[data-radix-popper-content-wrapper]")) return;
      onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  return createPortal(
    <div
      className={cn("fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4", className)}
      onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      {children}
    </div>,
    document.body,
  );
}
